import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import { useLibrary, useReadingChallenge } from '../../hooks/useLibrary';
import { useAutoShelves } from '../../hooks/useAutoShelves';

import { onCoverError } from '../../lib/coverFallback';
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export function notifyBookAdded(book) {
  window.dispatchEvent(new CustomEvent('reanlo:book-added', { detail: book }));
}

const EMOJI_OPTIONS = ['📚','⚡','💔','☕','🔥','🌙','💀','🦋','🐉','💕','🏆','🎭','✨','🌊'];
const SHELF_LABELS = {
  tbr: { emoji: '📚', label: 'To Read' },
  reading: { emoji: '📖', label: 'Reading' },
  read: { emoji: '✅', label: 'Read' },
  loved: { emoji: '❤️', label: 'Loved' },
};

export default function LibraryDock() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [activeShelf, setActiveShelf] = useState(null);
  const [smartShelves, setSmartShelves] = useState([]);
  const [customShelves, setCustomShelves] = useState([]);
  const [hoveredBook, setHoveredBook] = useState(null);
  const [autoShelfMap, setAutoShelfMap] = useState({});
  const [customShelfMap, setCustomShelfMap] = useState({});
  const [showAllAuto, setShowAllAuto] = useState(false);

  const [shelfPrompt, setShelfPrompt] = useState(null);
  const [selectedShelves, setSelectedShelves] = useState([]);
  const [creatingShelf, setCreatingShelf] = useState(false);
  const [newShelfName, setNewShelfName] = useState('');
  const [newShelfEmoji, setNewShelfEmoji] = useState('📚');
  const [savingShelf, setSavingShelf] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  const { library, loading } = useLibrary();
  const { shelves: autoShelves } = useAutoShelves();
  const { challenge } = useReadingChallenge();
  const AUTO_SHELF_PREVIEW = 4;

  useEffect(() => {
    function handleBookAdded(e) {
      const book = e.detail;
      setShelfPrompt(book);
      setSelectedShelves([]);
      setCreatingShelf(false);
      setNewShelfName('');
      setExpanded(false);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 600);
    }
    window.addEventListener('reanlo:book-added', handleBookAdded);
    return () => window.removeEventListener('reanlo:book-added', handleBookAdded);
  }, []);

  useEffect(() => {
    if (library.length === 0) return;
    const groups = library.reduce((acc, item) => {
      const shelf = item.shelf || 'other';
      acc[shelf] = (acc[shelf] || 0) + 1;
      return acc;
    }, {});
    setSmartShelves([
      { emoji: '📚', name: 'To Read', key: 'tbr', count: groups.tbr || 0 },
      { emoji: '📖', name: 'Reading', key: 'reading', count: groups.reading || 0 },
      { emoji: '✅', name: 'Read', key: 'read', count: groups.read || 0 },
      { emoji: '❤️', name: 'Loved', key: 'loved', count: groups.loved || 0 },
    ].filter(s => s.count > 0));
  }, [library]);

  useEffect(() => { fetchCustomShelves(); }, []);

  async function fetchCustomShelves() {
    const user = await getCurrentUser();
    if (!user) return;
    const { data } = await supabase.from('custom_shelves').select('id, name, emoji, color').eq('user_id', user.id);
    if (data) {
      const withCounts = await Promise.all(data.map(async shelf => {
        const { count } = await supabase.from('user_library').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('custom_shelf_id', shelf.id);
        return { ...shelf, count: count || 0 };
      }));
      setCustomShelves(withCounts);
    }
  }

  useEffect(() => {
    async function fetchMaps() {
      const user = await getCurrentUser();
      if (!user) return;
      const { data: autoData } = await supabase.from('user_library_auto_shelf_entries').select('genre, user_library_id').eq('user_id', user.id);
      if (autoData) {
        const map = {};
        autoData.forEach(row => { if (!map[row.genre]) map[row.genre] = new Set(); map[row.genre].add(row.user_library_id); });
        setAutoShelfMap(map);
      }
      const { data: customData } = await supabase.from('user_library').select('id, custom_shelf_id').eq('user_id', user.id).not('custom_shelf_id', 'is', null);
      if (customData) {
        const map = {};
        customData.forEach(row => { if (!map[row.custom_shelf_id]) map[row.custom_shelf_id] = new Set(); map[row.custom_shelf_id].add(row.id); });
        setCustomShelfMap(map);
      }
    }
    fetchMaps();
  }, [library]);

  const allBooks = library.map(item => {
    const book = item.books || item.csv_book_imports;
    return {
      id: book?.id, libraryId: item.id, customShelfId: item.custom_shelf_id,
      title: book?.title || 'Unknown', author: book?.author_name || 'Unknown',
      coverUrl: book?.cover_image_url, shelf: item.shelf, color: getColor(book?.id),
      tropes: item.books?.book_tags?.map(bt => bt.tropes?.canonical_name).filter(Boolean) || [],
    };
  });

  const filteredBooks = activeShelf
    ? allBooks.filter(b => {
        const smart = smartShelves.find(s => s.name === activeShelf);
        if (smart) return b.shelf === smart.key;
        const autoIds = autoShelfMap[activeShelf];
        if (autoIds) return autoIds.has(b.libraryId);
        const custom = customShelves.find(s => s.name === activeShelf);
        if (custom) { const ids = customShelfMap[custom.id]; return ids ? ids.has(b.libraryId) : false; }
        return false;
      })
    : allBooks;

  const booksReadThisYear = library.filter(item =>
    (item.shelf === 'read' || item.shelf === 'loved') &&
    item.date_read && new Date(item.date_read).getFullYear() === new Date().getFullYear()
  ).length;
  const challengeProgress = challenge?.goal ? Math.round((booksReadThisYear / challenge.goal) * 100) : 0;

  function dismissPrompt() {
    setShelfPrompt(null); setSelectedShelves([]); setCreatingShelf(false); setNewShelfName('');
  }

  const toggleShelfSelection = (name) =>
    setSelectedShelves(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);

  const handleDoneAssigning = async () => {
    if (selectedShelves.length > 0 && shelfPrompt?.id) {
      const user = await getCurrentUser();
      if (user) {
        const { data: libEntry } = await supabase.from('user_library').select('id, shelf').eq('user_id', user.id).eq('book_id', shelfPrompt.id).single();
        if (libEntry) {
          const SMART_SHELF_KEYS = { 'To Read': 'tbr', 'Reading': 'reading', 'Read': 'read', 'Loved': 'loved' };
          for (const shelfName of selectedShelves) {
            const smartKey = SMART_SHELF_KEYS[shelfName];
            if (smartKey) await supabase.from('user_library').update({ shelf: smartKey }).eq('id', libEntry.id);
            else {
              const custom = customShelves.find(s => s.name === shelfName);
              if (custom) await supabase.from('user_library').update({ custom_shelf_id: custom.id }).eq('id', libEntry.id);
            }
          }
        }
      }
    }
    if (selectedShelves.length > 0) { setActiveShelf(selectedShelves[0]); setExpanded(true); }
    dismissPrompt();
    fetchCustomShelves();
  };

  const handleCreateShelf = async () => {
    if (!newShelfName.trim()) return;
    setSavingShelf(true);
    const user = await getCurrentUser();
    if (user) {
      const { data } = await supabase.from('custom_shelves').insert({ user_id: user.id, name: newShelfName.trim(), emoji: newShelfEmoji, color: 'var(--feedback-warning)' }).select().single();
      if (data) { setCustomShelves(prev => [...prev, { ...data, count: 0 }]); setSelectedShelves(prev => [...prev, newShelfName.trim()]); }
    }
    setNewShelfName(''); setCreatingShelf(false); setSavingShelf(false);
  };

  const allShelvesForPrompt = [...smartShelves, ...customShelves];
  const visibleAutoShelves = showAllAuto ? autoShelves : autoShelves.slice(0, AUTO_SHELF_PREVIEW);
  const bookAlreadyShelved = shelfPrompt?.shelf;
  const shelfInfo = bookAlreadyShelved ? SHELF_LABELS[bookAlreadyShelved] : null;

  // Responsive padding
  const sidePad = isMobile ? '0 16px' : '0 60px';
  const trayPad = isMobile ? '14px 16px' : '20px 60px 16px';
  const promptPad = isMobile ? '12px 16px' : '14px 60px';

  if (loading) return null;

  return (
    <>
      {shelfPrompt && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={dismissPrompt} />}

      {/* ── Shelf Assignment Prompt ── */}
      {shelfPrompt && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'fixed', bottom: 52, left: 0, right: 0, zIndex: 100,
          background: 'var(--brand-white)', borderTop: '2px solid var(--feedback-warning)',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.12)',
          animation: 'dockSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          maxHeight: isMobile ? '60vh' : 'auto', overflowY: isMobile ? 'auto' : 'visible',
        }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: promptPad }}>

            {isMobile ? (
              /* ── Mobile prompt: compact stacked ── */
              <div>
                {/* Book confirmation row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-10)' }}>
                  <div style={{
                    width: 36, height: 52, background: shelfPrompt.coverUrl ? 'none' : `linear-gradient(135deg, ${shelfPrompt.color || '#9E4B2C'}, ${shelfPrompt.color || '#9E4B2C'}cc)`,
                    borderRadius: '2px 5px 5px 2px', overflow: 'hidden', flexShrink: 0,
                    animation: justAdded ? 'bookPopIn 0.4s ease-out' : 'none',
                  }}>
                    {shelfPrompt.coverUrl && <img src={shelfPrompt.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginBottom: 1 }}>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: '#22c55e', fontWeight: 700 }}>✓</span>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shelfPrompt.title}</p>
                    </div>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
                      {shelfInfo ? `Added to ${shelfInfo.emoji} ${shelfInfo.label}` : 'Added to library'}
                    </p>
                  </div>
                  <button onClick={dismissPrompt} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', padding: 'var(--space-xs)' }}>✕</button>
                </div>

                {/* If already shelved — confirmation only */}
                {bookAlreadyShelved ? (
                  <button onClick={dismissPrompt}
                    style={{ width: '100%', padding: '9px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-500)', cursor: 'pointer' }}>
                    Done
                  </button>
                ) : !creatingShelf ? (
                  <>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>Add to a shelf?</p>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-10)' }}>
                      {allShelvesForPrompt.map((s, i) => {
                        const isSelected = selectedShelves.includes(s.name);
                        return (
                          <button key={i} onClick={() => toggleShelfSelection(s.name)} style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '5px 10px',
                            borderRadius: 'var(--radius-xl)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: isSelected ? 600 : 500,
                            background: isSelected ? 'var(--neutral-800)' : 'var(--brand-white)', color: isSelected ? 'var(--brand-paper)' : 'var(--neutral-600)',
                            border: isSelected ? '1.5px solid var(--neutral-800)' : '1px solid var(--brand-sand)',
                          }}>
                            {isSelected && <span style={{ fontSize: 'var(--font-size-tiny)' }}>✓</span>}
                            {s.emoji} {s.name}
                          </button>
                        );
                      })}
                      <button onClick={() => setCreatingShelf(true)} style={{
                        padding: '5px 10px', borderRadius: 'var(--radius-xl)', border: '1px dashed var(--feedback-warning)',
                        background: 'transparent', color: 'var(--feedback-warning)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
                      }}>+ New</button>
                    </div>
                    <button onClick={handleDoneAssigning} style={{
                      width: '100%', padding: '9px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: selectedShelves.length > 0 ? 'var(--neutral-800)' : 'var(--neutral-100)',
                      color: selectedShelves.length > 0 ? 'var(--brand-paper)' : 'var(--neutral-500)',
                    }}>
                      {selectedShelves.length > 0 ? `Add to ${selectedShelves.length} shelf${selectedShelves.length > 1 ? 'es' : ''}` : 'Skip'}
                    </button>
                  </>
                ) : (
                  <div>
                    <input value={newShelfName} onChange={e => setNewShelfName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreateShelf()}
                      placeholder="Shelf name..." autoFocus
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', marginBottom: 'var(--space-sm)' }} />
                    <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                      <button onClick={handleCreateShelf} disabled={!newShelfName.trim() || savingShelf}
                        style={{ flex: 1, padding: 'var(--space-sm)', background: newShelfName.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', color: newShelfName.trim() ? 'var(--brand-paper)' : 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>
                        {savingShelf ? 'Creating...' : 'Create & add'}
                      </button>
                      <button onClick={() => setCreatingShelf(false)}
                        style={{ padding: '8px 12px', background: 'none', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── Desktop prompt: horizontal ── */
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-lg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexShrink: 0 }}>
                  <div style={{
                    width: 42, height: 60,
                    background: shelfPrompt.coverUrl ? 'none' : `linear-gradient(135deg, ${shelfPrompt.color || '#9E4B2C'}, ${shelfPrompt.color || '#9E4B2C'}cc)`,
                    borderRadius: '2px 6px 6px 2px', boxShadow: '2px 2px 8px rgba(0,0,0,0.2)',
                    overflow: 'hidden', flexShrink: 0,
                    animation: justAdded ? 'bookPopIn 0.4s ease-out' : 'none',
                  }}>
                    {shelfPrompt.coverUrl && <img src={shelfPrompt.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 'var(--space-xxs)' }}>
                      <span style={{ fontSize: 'var(--font-size-base)', color: '#22c55e', fontWeight: 700 }}>✓</span>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shelfPrompt.title}</p>
                    </div>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
                      {shelfInfo ? `Added to ${shelfInfo.emoji} ${shelfInfo.label}` : 'Added to library'}
                    </p>
                  </div>
                </div>
                <div style={{ width: 1, height: 52, background: 'var(--brand-sand)', flexShrink: 0 }} />
                {bookAlreadyShelved ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '8px 16px', background: 'var(--feedback-success-bg)', border: '1px solid #bbf7d0', borderRadius: 'var(--radius-10)' }}>
                      <span style={{ fontSize: 'var(--font-size-xl)' }}>{shelfInfo?.emoji}</span>
                      <div><p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: '#166534', margin: 'var(--space-none)' }}>{shelfInfo?.label}</p><p style={{ fontSize: 'var(--font-size-xs)', color: '#4ade80', margin: 'var(--space-none)' }}>Shelf updated</p></div>
                    </div>
                    <button onClick={dismissPrompt} style={{ padding: '8px 16px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-500)', cursor: 'pointer' }}>Done</button>
                  </div>
                ) : (
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>Add to a shelf?</p>
                    {!creatingShelf ? (
                      <>
                        <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap', marginBottom: 'var(--space-10)' }}>
                          {allShelvesForPrompt.map((s, i) => {
                            const isSelected = selectedShelves.includes(s.name);
                            return (
                              <button key={i} onClick={() => toggleShelfSelection(s.name)} style={{
                                display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                                borderRadius: 'var(--radius-xxl)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: isSelected ? 600 : 500,
                                background: isSelected ? 'var(--neutral-800)' : 'var(--brand-white)', color: isSelected ? 'var(--brand-paper)' : 'var(--neutral-600)',
                                border: isSelected ? '1.5px solid var(--neutral-800)' : '1px solid var(--brand-sand)', transition: 'all 0.15s',
                              }}>
                                {isSelected && <span style={{ fontSize: 'var(--font-size-xxs)' }}>✓</span>}
                                {s.emoji} {s.name}
                              </button>
                            );
                          })}
                          <button onClick={() => setCreatingShelf(true)} style={{ padding: '6px 12px', borderRadius: 'var(--radius-xxl)', border: '1px dashed var(--feedback-warning)', background: 'transparent', color: 'var(--feedback-warning)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>+ New shelf</button>
                        </div>
                        <button onClick={handleDoneAssigning} style={{
                          padding: '8px 20px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', border: 'none',
                          background: selectedShelves.length > 0 ? 'var(--neutral-800)' : 'var(--neutral-100)',
                          color: selectedShelves.length > 0 ? 'var(--brand-paper)' : 'var(--neutral-500)',
                        }}>
                          {selectedShelves.length > 0 ? `Add to ${selectedShelves.length} shelf${selectedShelves.length > 1 ? 'es' : ''}` : 'Skip'}
                        </button>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', flex: 1 }}>
                          {EMOJI_OPTIONS.map((e, i) => (
                            <button key={i} onClick={() => setNewShelfEmoji(e)} style={{ width: 44, height: 44, borderRadius: 'var(--radius-10)', cursor: 'pointer', fontSize: 22, border: newShelfEmoji === e ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: newShelfEmoji === e ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-none)', transition: 'all 0.1s', flexShrink: 0 }}>{e}</button>
                          ))}
                        </div>
                        <div>
                          <input value={newShelfName} onChange={e => setNewShelfName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateShelf()} placeholder="Shelf name..." autoFocus
                            style={{ padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-base)', outline: 'none', marginBottom: 'var(--space-sm)', width: 200, display: 'block' }} />
                          <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
                            <button onClick={handleCreateShelf} disabled={!newShelfName.trim() || savingShelf} style={{ padding: '7px 16px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', border: 'none', background: newShelfName.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)', color: newShelfName.trim() ? 'var(--brand-paper)' : 'var(--neutral-400)' }}>{savingShelf ? 'Creating...' : 'Create & add'}</button>
                            <button onClick={() => setCreatingShelf(false)} style={{ padding: '7px 12px', background: 'none', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', cursor: 'pointer' }}>Cancel</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Expanded Tray ── */}
      {expanded && !shelfPrompt && (
        <div style={{ position: 'fixed', bottom: 52, left: 0, right: 0, zIndex: 99, background: 'var(--brand-white)', borderTop: '1px solid var(--brand-sand)', boxShadow: '0 -8px 40px rgba(0,0,0,0.1)', maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: trayPad }}>
            {allBooks.length > 0 ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-xl)', marginBottom: 'var(--space-lg)', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 1.5, textTransform: 'uppercase', margin: '0 0 8px' }}>Your Shelves</p>
                    <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                      <button onClick={() => setActiveShelf(null)} style={pillStyle(!activeShelf)}>All ({allBooks.length})</button>
                      {smartShelves.map((s, i) => (
                        <button key={i} onClick={() => setActiveShelf(activeShelf === s.name ? null : s.name)} style={pillStyle(activeShelf === s.name)}>
                          {s.emoji} {s.name} ({s.count})
                        </button>
                      ))}
                      {autoShelves.length > 0 && (
                        <>
                          <div style={{ width: 1, height: 28, background: 'var(--brand-sand)', margin: '0 2px', alignSelf: 'center' }} />
                          {visibleAutoShelves.map((shelf, i) => (
                            <button key={i} onClick={() => setActiveShelf(activeShelf === shelf.label ? null : shelf.label)} style={pillStyle(activeShelf === shelf.label)}>
                              {shelf.source === 'genre' ? '📂' : '🏷️'} {shelf.label} ({shelf.count})
                            </button>
                          ))}
                          {autoShelves.length > AUTO_SHELF_PREVIEW && (
                            <button onClick={() => setShowAllAuto(p => !p)} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: '5px 4px' }}>
                              {showAllAuto ? '▲ less' : `+${autoShelves.length - AUTO_SHELF_PREVIEW} more`}
                            </button>
                          )}
                        </>
                      )}
                      {customShelves.length > 0 && (
                        <>
                          <div style={{ width: 1, height: 28, background: 'var(--brand-sand)', margin: '0 2px', alignSelf: 'center' }} />
                          {customShelves.map((s, i) => (
                            <button key={i} onClick={() => setActiveShelf(activeShelf === s.name ? null : s.name)} style={{ fontSize: 'var(--font-size-sm)', padding: '5px 14px', borderRadius: 'var(--radius-xxl)', cursor: 'pointer', border: activeShelf === s.name ? `1.5px solid ${s.color || '#d97706'}` : '1px solid var(--feedback-warning-border)', background: activeShelf === s.name ? `${s.color || '#d97706'}15` : 'var(--brand-white)', color: 'var(--feedback-warning-ink)', fontWeight: activeShelf === s.name ? 700 : 500 }}>
                              {s.emoji} {s.name} ({s.count})
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                  {!isMobile && challenge?.goal > 0 && (
                    <div style={{ flexShrink: 0, background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', padding: '10px 14px', border: '1px solid var(--brand-sand)', minWidth: 180 }}>
                      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 1.5, textTransform: 'uppercase', margin: '0 0 6px' }}>{new Date().getFullYear()} Challenge</p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)', fontWeight: 600 }}>{booksReadThisYear} / {challenge.goal}</span>
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--feedback-warning)' }}>{Math.min(challengeProgress, 100)}%</span>
                      </div>
                      <div style={{ height: 4, background: 'var(--brand-sand)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'linear-gradient(90deg, var(--feedback-warning), #f59e0b)', width: `${Math.min(challengeProgress, 100)}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: isMobile ? 10 : 14, overflowX: 'auto', paddingBottom: 'var(--space-sm)', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                  {(filteredBooks.length > 0 ? filteredBooks : allBooks).map((book, i) => {
                    const isHov = hoveredBook === i;
                    const bookW = isMobile ? 70 : 90;
                    const bookH = isMobile ? 100 : 130;
                    return (
                      <div key={i} onClick={() => book.id && navigate(`/book/${book.id}`)} onMouseEnter={() => setHoveredBook(i)} onMouseLeave={() => setHoveredBook(null)}
                        style={{ flexShrink: 0, width: bookW, textAlign: 'center', cursor: 'pointer', scrollSnapAlign: 'start', transition: 'transform 0.2s cubic-bezier(0.34,1.56,0.64,1)', transform: isHov ? 'translateY(-6px)' : 'none' }}>
                        <div style={{ width: bookW, height: bookH, borderRadius: '4px 8px 8px 4px', overflow: 'hidden', background: book.coverUrl ? 'none' : `linear-gradient(150deg, ${book.color}ee, ${book.color})`, boxShadow: isHov ? `0 8px 20px ${book.color}50` : '0 2px 8px rgba(0,0,0,0.12)', transition: 'box-shadow 0.2s', position: 'relative' }}>
                          {book.coverUrl ? <img src={book.coverUrl} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} /> : (
                            <>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: 'rgba(0,0,0,0.25)' }} />
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 6px 6px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                                <p style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 8 : 10, fontWeight: 700, color: 'var(--brand-white)', margin: 'var(--space-none)', lineHeight: 1.2 }}>{book.title}</p>
                              </div>
                            </>
                          )}
                        </div>
                        <p style={{ fontSize: isMobile ? 9 : 10, fontWeight: 600, color: 'var(--neutral-800)', margin: '4px 0 1px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.title}</p>
                        <p style={{ fontSize: isMobile ? 8 : 9, color: 'var(--neutral-500)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.author}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: 'var(--font-size-h3)', margin: '0 0 8px' }}>📚</p>
                <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>Your library is empty</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Add books from Match Me or the community</p>
              </div>
            )}
          </div>
        </div>
      )}

      {expanded && !shelfPrompt && <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setExpanded(false)} />}

      {/* ── Dock Bar ── */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        background: 'var(--neutral-800)', borderTop: '1px solid var(--neutral-700)',
        height: 52, display: 'flex', alignItems: 'center',
        padding: sidePad, cursor: shelfPrompt ? 'default' : 'pointer', userSelect: 'none',
      }} onClick={() => { if (!shelfPrompt) setExpanded(p => !p); }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
            <span style={{ fontSize: 'var(--font-size-md)' }}>📚</span>
            <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: 'var(--brand-paper)' }}>Your Books</span>
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', background: 'var(--neutral-700)', padding: '1px 7px', borderRadius: 'var(--radius-10)' }}>{allBooks.length}</span>
          </div>

          {allBooks.slice(0, isMobile ? 3 : 5).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {allBooks.slice(0, isMobile ? 3 : 5).map((b, i) => (
                <div key={i} style={{ width: isMobile ? 20 : 26, height: isMobile ? 28 : 36, borderRadius: 3, flexShrink: 0, overflow: 'hidden', background: b.coverUrl ? 'none' : `linear-gradient(135deg, ${b.color}, ${b.color}cc)`, marginLeft: i > 0 ? -6 : 0, border: '1.5px solid var(--neutral-800)', boxShadow: '0 1px 4px rgba(0,0,0,0.4)', position: 'relative', zIndex: 5 - i }}>
                  {b.coverUrl && <img src={b.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />}
                </div>
              ))}
            </div>
          )}

          {!isMobile && challenge?.goal > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', background: 'var(--neutral-700)', borderRadius: 'var(--radius-xxl)', padding: '3px 10px' }}>
              <span style={{ fontSize: 'var(--font-size-xxs)' }}>🎯</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--brand-paper)', fontWeight: 600 }}>{booksReadThisYear}/{challenge.goal}</span>
              <div style={{ width: 40, height: 3, background: 'var(--neutral-600)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--feedback-warning)', width: `${Math.min(challengeProgress, 100)}%` }} />
              </div>
              <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', fontWeight: 700 }}>{Math.min(challengeProgress, 100)}%</span>
            </div>
          )}

          {activeShelf && <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', fontWeight: 600 }}>· {activeShelf}</span>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginLeft: 'auto' }}>
            {!isMobile && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontWeight: 500 }}>
                {shelfPrompt ? 'Added to library' : expanded ? 'Collapse' : 'Expand'}
              </span>
            )}
            {!shelfPrompt && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', transition: 'transform 0.2s', display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'none' }}>▴</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes dockSlideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes bookPopIn { 0% { transform: scale(0.5) rotate(-5deg); opacity: 0; } 60% { transform: scale(1.15) rotate(2deg); opacity: 1; } 100% { transform: scale(1) rotate(0); } }
      `}</style>
    </>
  );
}

function pillStyle(active) {
  return {
    fontSize: 'var(--font-size-sm)', padding: '5px 14px', borderRadius: 'var(--radius-xxl)', cursor: 'pointer',
    border: active ? '1.5px solid var(--feedback-warning)' : '1px solid var(--brand-sand)',
    background: active ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
    color: active ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)',
    fontWeight: active ? 700 : 500,
  };
}

function getColor(id) {
  const colors = ['#9E4B2C','#384D3D','#d97706','#92400e','#78716c','#4a1a2e','#0d47a1','#b8860b'];
  return colors[(id || 0) % colors.length];
}